/**
 * Employee profile — full page with photo + tabs.
 *
 * Tabs: Overview · Attendance · Leaves · Payments · Invoices · History.
 *
 * Mock-aware. Edits are kept in local state so the UI feels live without
 * any backend calls. Once InstantDB is wired this page swaps the local
 * mutators for InstantDB transactions.
 */

import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Banknote,
  Building2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  ShieldOff,
} from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneyCompact } from "@bizop/shared/currency";
import { formatDate, formatRelative } from "@bizop/shared/dates";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { AttendanceCalendar } from "../../../components/workforce/AttendanceCalendar";
import { EmployeeKpiStrip } from "../../../components/workforce/EmployeeKpiStrip";
import {
  EmployeeForm,
  type EmployeeFormValues,
} from "../../../components/forms/EmployeeForm";
import {
  LeaveRequestForm,
  type LeaveRequestFormValues,
} from "../../../components/forms/LeaveRequestForm";
import {
  EmployeePaymentForm,
  type EmployeePaymentFormValues,
} from "../../../components/forms/EmployeePaymentForm";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  RequirePermission,
  useHasPermission,
} from "../../../lib/permissions";
import {
  AttachmentBadge,
  AttachmentList,
} from "../../../components/attachments";
import {
  WORKFORCE_BRANCHES,
  kpisForEmployee,
  mockAttendance,
  mockEmployeeInvoices,
  mockEmployeePayments,
  mockEmployees,
  mockLeaves,
  monthsTenure,
  type MockAttendance,
  type MockEmployee,
  type MockEmployeeInvoice,
  type MockEmployeePayment,
  type MockLeave,
  type MockLeaveStatus,
  type MockPaymentStatus,
} from "../../../lib/workforceMock";

const EMPLOYEE_PURPOSE_OPTIONS = [
  { value: "nid", label: "National ID" },
  { value: "passport", label: "Passport" },
  { value: "cv", label: "CV / Resume" },
  { value: "offer_letter", label: "Offer Letter" },
  { value: "appointment_letter", label: "Appointment Letter" },
  { value: "other", label: "Other" },
];

export const Route = createFileRoute("/_app/workforce/employees/$employeeId")({
  component: EmployeeProfilePage,
});

const TAB_VALUES = [
  "overview",
  "attendance",
  "leaves",
  "payments",
  "invoices",
  "documents",
  "history",
] as const;
type TabValue = (typeof TAB_VALUES)[number];

const LEAVE_STATUS_VARIANT: Record<
  MockLeaveStatus,
  "info" | "success" | "danger" | "warning" | "neutral"
> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
};

const PAYMENT_STATUS_VARIANT: Record<
  MockPaymentStatus,
  "info" | "success" | "danger" | "warning" | "neutral"
> = {
  draft: "info",
  paid: "success",
  void: "neutral",
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

function EmployeeProfilePage() {
  const { employeeId } = Route.useParams();
  const navigate = useNavigate();
  const isMock = useMockData();
  void isMock;

  const [allEmployees, setAllEmployees] = usePersistentMockState<MockEmployee[]>(
    "employees",
    mockEmployees,
  );
  const [allLeaves, setAllLeaves] = usePersistentMockState<MockLeave[]>(
    "leaves",
    mockLeaves,
  );
  const [allPayments, setAllPayments] = usePersistentMockState<MockEmployeePayment[]>(
    "employeePayments",
    mockEmployeePayments,
  );
  const [allAttendance] = usePersistentMockState<MockAttendance[]>(
    "attendance",
    mockAttendance,
  );

  const employee = useMemo<MockEmployee | null>(
    () => allEmployees.find((e) => e._id === employeeId) ?? null,
    [allEmployees, employeeId],
  );
  const [tab, setTab] = useState<TabValue>("overview");
  const [editing, setEditing] = useState(false);

  const leaves = useMemo<MockLeave[]>(
    () =>
      employee
        ? allLeaves
            .filter((l) => l.employeeId === employee._id)
            .sort((a, b) => b.fromDate - a.fromDate)
        : [],
    [allLeaves, employee],
  );
  const payments = useMemo<MockEmployeePayment[]>(
    () =>
      employee
        ? allPayments
            .filter((p) => p.employeeId === employee._id)
            .sort((a, b) => b.periodFrom - a.periodFrom)
        : [],
    [allPayments, employee],
  );
  const attendance = useMemo<MockAttendance[]>(
    () =>
      employee
        ? allAttendance
            .filter((a) => a.employeeId === employee._id)
            .sort((a, b) => b.date - a.date)
        : [],
    [allAttendance, employee],
  );
  // mockEmployeeInvoices has no DB entity yet — read directly.
  const invoices = useMemo<MockEmployeeInvoice[]>(
    () =>
      employee
        ? mockEmployeeInvoices
            .filter((i) => i.employeeId === employee._id)
            .sort((a, b) => b.occurredAt - a.occurredAt)
        : [],
    [employee],
  );
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const canSeeFinance = useHasPermission("finance.read");
  const canTerminate = useHasPermission("employees.terminate");

  if (!employee) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Employee not found"
          actions={
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/workforce/employees" })}
            >
              <ArrowLeft className="h-4 w-4" /> Back to employees
            </Button>
          }
        />
        <EmptyState
          title="Unknown employee"
          description={`No employee with id ${employeeId}.`}
        />
      </div>
    );
  }

  const kpis = kpisForEmployee(employee._id);

  // Profile-page KPIs derived from the live (mock) state
  const tenure = monthsTenure(employee.hiredAt);
  const ytdEarnings = canSeeFinance
    ? formatMoneyCompact(kpis.totalEarnedYTD, "BDT", { locale: "en-BD" })
    : "—";
  const attendancePct = `${Math.round(kpis.attendanceRate * 100)}%`;
  const overtimeHours = `${(kpis.overtimeMinutes / 60).toFixed(1)}h`;
  const pendingLeaves = leaves.filter((l) => l.status === "pending").length;

  function handleEditSubmit(values: EmployeeFormValues) {
    if (!employee) return;
    const updated: MockEmployee = {
      ...employee,
      fullName: values.fullName,
      displayName: values.displayName || values.fullName,
      photoUrl: values.photoUrl,
      employmentType: values.employmentType,
      department: values.department,
      branchId: values.branchId,
      branchLabel:
        WORKFORCE_BRANCHES.find((b) => b.id === values.branchId)?.displayName ?? null,
      jobTitle: values.jobTitle,
      hiredAt: new Date(values.hiredAt).getTime(),
      baseSalaryCents: values.baseSalaryCents,
      salaryCurrency: values.salaryCurrency,
      payCadence: values.payCadence,
      nationalId: values.nationalId || null,
      phone: values.phone,
      email: values.email || null,
      addressLine1: values.addressLine1,
      addressLine2: values.addressLine2 || null,
      addressCity: values.addressCity,
      addressDistrict: values.addressDistrict,
      addressPostalCode: values.addressPostalCode || null,
      emergencyContact: values.emergencyContact,
      bankAccount: values.bankAccount,
      notes: values.notes || null,
      updatedAt: Date.now(),
    };
    setAllEmployees((prev) => prev.map((e) => (e._id === updated._id ? updated : e)));
    setEditing(false);
    toast.success(`Updated ${employee.fullName}`);
  }

  function handleTerminate() {
    if (!employee) return;
    if (employee.terminatedAt !== null) return;
    const terminatedAt = Date.now();
    const id = employee._id;
    setAllEmployees((prev) =>
      prev.map((e) =>
        e._id === id
          ? { ...e, isActive: false, terminatedAt, updatedAt: terminatedAt }
          : e,
      ),
    );
    toast.success(`${employee.fullName} terminated`);
  }

  function handleNewLeave(values: LeaveRequestFormValues) {
    const now = Date.now();
    const newLeave: MockLeave = {
      _id: `leave_local_${now}`,
      employeeId: employee!._id,
      leaveType: values.leaveType,
      fromDate: new Date(values.fromDate).getTime(),
      toDate: new Date(values.toDate).getTime(),
      days: values.days,
      reason: values.reason || null,
      status: "pending",
      approvedByUserId: null,
      approvedAt: null,
      rejectionReason: null,
    };
    setAllLeaves((prev) => [newLeave, ...prev]);
    setLeaveOpen(false);
    toast.success(`Leave request filed (${values.days} day${values.days === 1 ? "" : "s"})`);
  }

  function handleNewPayment(values: EmployeePaymentFormValues) {
    if (!employee) return;
    const status: MockPaymentStatus = values.markPaid ? "paid" : "draft";
    const now = Date.now();
    const newPay: MockEmployeePayment = {
      _id: `pay_local_${now}_${Math.random().toString(36).slice(2, 7)}`,
      employeeId: employee._id,
      paymentType: values.paymentType,
      periodFrom: new Date(values.periodFrom).getTime(),
      periodTo: new Date(values.periodTo).getTime(),
      baseSalaryCents: values.baseSalaryCents,
      overtimeCents: values.overtimeCents,
      bonusCents: values.bonusCents,
      deductionsCents: values.deductionsCents,
      netCents: values.netCents,
      currency: values.currency,
      fxRateToBaseE8: 100_000_000n,
      netInBaseCents: values.netCents,
      paidAt: status === "paid" ? new Date(values.paidAt).getTime() : null,
      paymentMethod: values.paymentMethod,
      referenceNumber: values.referenceNumber || null,
      status,
      notes: values.notes || null,
    };
    setAllPayments((prev) => [newPay, ...prev]);
    setPaymentOpen(false);
    toast.success(
      status === "paid"
        ? `Payment marked as paid for ${employee.fullName}.`
        : `Payment recorded for ${employee.fullName}.`,
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={employee.fullName}
        description={[
          employee.code,
          employee.jobTitle,
          employee.department,
          employee.branchLabel,
        ]
          .filter(Boolean)
          .join(" · ")}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/workforce/employees" })}
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            {employee.isActive && employee.terminatedAt === null ? (
              <RequirePermission permission="payroll.write">
                <Button onClick={() => setPaymentOpen(true)}>
                  <Banknote className="h-3.5 w-3.5" /> Pay
                </Button>
              </RequirePermission>
            ) : null}
            <RequirePermission permission="employees.write">
              <Button variant="ghost" size="icon" onClick={() => setEditing(true)} aria-label="Edit">
                <Pencil className="size-4" />
              </Button>
            </RequirePermission>
            {canTerminate && employee.terminatedAt === null ? (
              <Button
                variant="outline"
                onClick={handleTerminate}
              >
                <ShieldOff className="h-3.5 w-3.5" /> Terminate
              </Button>
            ) : null}
          </div>
        }
      />

      {/* Header card with photo + status */}
      <section
        className={cn(
          "flex items-center gap-4 rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-4",
        )}
      >
        {employee.photoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={employee.photoUrl}
            alt={`${employee.fullName} avatar`}
            className="h-16 w-16 rounded-full object-cover border border-(--border-subtle)"
          />
        ) : (
          <div
            aria-hidden
            className="h-16 w-16 rounded-full grid place-items-center bg-(--bg-canvas) border border-(--border-subtle) text-(--text-secondary) font-medium text-[18px]"
          >
            {initials(employee.fullName)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-(--text-tertiary)">
              {employee.code}
            </span>
            {employee.terminatedAt !== null ? (
              <StatusPill variant="neutral" size="sm">
                Terminated
              </StatusPill>
            ) : employee.isActive ? (
              <StatusPill variant="success" size="sm">
                Active
              </StatusPill>
            ) : (
              <StatusPill variant="warning" size="sm">
                Inactive
              </StatusPill>
            )}
            {employee.hiredAtIsOverride ? (
              <Badge variant="info" size="sm" title="Hired date manually overridden">
                hired-date override
              </Badge>
            ) : null}
          </div>
          <div className="text-[14px] font-medium text-(--text-primary)">
            {employee.fullName}
          </div>
          <div className="text-[12px] text-(--text-secondary)">
            {employee.jobTitle} — {employee.department}
            {employee.branchLabel ? ` · ${employee.branchLabel}` : ""}
          </div>
        </div>
      </section>

      <EmployeeKpiStrip
        monthsTenure={tenure}
        ytdEarnings={ytdEarnings}
        attendancePct={attendancePct}
        overtimeHours={overtimeHours}
        pendingLeaves={pendingLeaves}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leaves">Leaves</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-1.5">
            Documents{" "}
            <AttachmentBadge
              parentTable="employees"
              parentRecordId={employee._id}
            />
          </TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="pt-4">
          <OverviewTab employee={employee} canSeeFinance={canSeeFinance} />
        </TabsContent>

        <TabsContent value="attendance" className="pt-4 flex flex-col gap-4">
          <AttendanceCalendar attendance={attendance} />
          <AttendanceMonthlySummary attendance={attendance} />
        </TabsContent>

        <TabsContent value="leaves" className="pt-4 flex flex-col gap-3">
          <div className="flex justify-end">
            <RequirePermission permission="employees.write">
              <Button onClick={() => setLeaveOpen(true)}>
                <Plus className="h-4 w-4" /> Request leave on behalf
              </Button>
            </RequirePermission>
          </div>
          <LeavesTable leaves={leaves} />
        </TabsContent>

        <TabsContent value="payments" className="pt-4 flex flex-col gap-3">
          <div className="flex justify-end">
            <RequirePermission permission="payroll.write">
              <Button onClick={() => setPaymentOpen(true)}>
                <Plus className="h-4 w-4" /> Record payment
              </Button>
            </RequirePermission>
          </div>
          <PaymentsTable payments={payments} canSeeFinance={canSeeFinance} />
        </TabsContent>

        <TabsContent value="invoices" className="pt-4">
          <InvoicesTable invoices={invoices} />
        </TabsContent>

        <TabsContent value="documents" className="pt-4">
          <AttachmentList
            parentTable="employees"
            parentRecordId={employee._id}
            purposeOptions={EMPLOYEE_PURPOSE_OPTIONS}
            mimeAllowlist={["image/*", "application/pdf"]}
            title="Drop employee documents here"
            description="National ID, passport, CV, offer & appointment letters. PII — keep tight."
          />
        </TabsContent>

        <TabsContent value="history" className="pt-4">
          <HistoryTab />
        </TabsContent>
      </Tabs>

      {/* Edit sheet */}
      <Sheet open={editing} onOpenChange={setEditing}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>Edit {employee.fullName}</SheetTitle>
            <SheetDescription>
              Updates apply immediately and are recorded in the audit log.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <EmployeeForm
              isEdit
              branches={WORKFORCE_BRANCHES}
              initialValues={{
                code: employee.code,
                fullName: employee.fullName,
                displayName: employee.displayName,
                photoUrl: employee.photoUrl,
                employmentType: employee.employmentType,
                department: employee.department,
                branchId: employee.branchId,
                jobTitle: employee.jobTitle,
                hiredAt: new Date(employee.hiredAt).toISOString().slice(0, 10),
                baseSalaryCents: employee.baseSalaryCents,
                salaryCurrency: employee.salaryCurrency,
                payCadence: employee.payCadence,
                nationalId: employee.nationalId ?? "",
                phone: employee.phone,
                email: employee.email ?? "",
                addressLine1: employee.addressLine1,
                addressLine2: employee.addressLine2 ?? "",
                addressCity: employee.addressCity,
                addressDistrict: employee.addressDistrict,
                addressPostalCode: employee.addressPostalCode ?? "",
                emergencyContact: employee.emergencyContact,
                bankAccount: employee.bankAccount,
                notes: employee.notes ?? "",
              }}
              onSubmit={handleEditSubmit}
              onCancel={() => setEditing(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Leave sheet */}
      <Sheet open={leaveOpen} onOpenChange={setLeaveOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Request leave on behalf</SheetTitle>
            <SheetDescription>For {employee.fullName}.</SheetDescription>
          </SheetHeader>
          <SheetBody>
            <LeaveRequestForm
              onSubmit={handleNewLeave}
              onCancel={() => setLeaveOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Payment sheet */}
      <Sheet open={paymentOpen} onOpenChange={setPaymentOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Pay {employee.fullName}</SheetTitle>
            <SheetDescription>
              {employee.code} · {employee.jobTitle} · pre-filled with the most
              recent pay period and base salary.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <EmployeePaymentForm
              employeeId={employee._id}
              initialValues={{
                baseSalaryCents: employee.baseSalaryCents,
                currency: employee.salaryCurrency,
                netCents: employee.baseSalaryCents,
              }}
              onSubmit={handleNewPayment}
              onCancel={() => setPaymentOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Overview tab
 * ───────────────────────────────────────────────────────────── */

function OverviewTab({
  employee,
  canSeeFinance,
}: {
  employee: MockEmployee;
  canSeeFinance: boolean;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Section title="Contact">
        <Field
          icon={<Phone className="h-3.5 w-3.5" />}
          label="Phone"
          value={employee.phone}
        />
        <Field
          icon={<Mail className="h-3.5 w-3.5" />}
          label="Email"
          value={employee.email ?? "—"}
        />
        <Field
          icon={<MapPin className="h-3.5 w-3.5" />}
          label="Address"
          value={
            [
              employee.addressLine1,
              employee.addressLine2,
              [
                employee.addressCity,
                employee.addressDistrict,
                employee.addressPostalCode,
              ]
                .filter(Boolean)
                .join(", "),
            ]
              .filter(Boolean)
              .join("\n") || "—"
          }
        />
        <Field
          icon={<Building2 className="h-3.5 w-3.5" />}
          label="National ID"
          value={employee.nationalId ?? "—"}
        />
      </Section>

      <Section title="Employment">
        <Field label="Department" value={employee.department} />
        <Field label="Branch" value={employee.branchLabel ?? "—"} />
        <Field
          label="Type"
          value={employee.employmentType.replace(/_/g, " ")}
        />
        <Field
          label="Hired on"
          value={
            <span className="inline-flex items-center gap-2">
              <span>
                {formatDate(employee.hiredAt, { style: "medium" })} (
                {formatRelative(employee.hiredAt)})
              </span>
              {employee.hiredAtIsOverride ? (
                <Badge variant="info" size="sm">
                  override
                </Badge>
              ) : null}
            </span>
          }
        />
        {employee.terminatedAt !== null ? (
          <Field
            label="Terminated"
            value={formatDate(employee.terminatedAt, { style: "medium" })}
          />
        ) : null}
      </Section>

      <Section title="Compensation">
        {canSeeFinance ? (
          <>
            <Field
              label="Base salary"
              value={`${formatMoney(
                employee.baseSalaryCents,
                employee.salaryCurrency,
                {
                  display: employee.salaryCurrency === "BDT" ? "symbol" : "code",
                  locale: "en-BD",
                },
              )} / ${employee.payCadence === "monthly" ? "month" : "two weeks"}`}
            />
            <Field label="Cadence" value={employee.payCadence} />
            <Field label="Currency" value={employee.salaryCurrency} />
          </>
        ) : (
          <div className="text-[12px] text-(--text-tertiary)">
            Salary is hidden — needs the finance.read permission.
          </div>
        )}
      </Section>

      <Section title="Bank account">
        <Field label="Bank" value={employee.bankAccount.bankName} />
        <Field label="Branch" value={employee.bankAccount.branch} />
        <Field
          label="Account name"
          value={employee.bankAccount.accountName}
        />
        <Field
          label="Account number"
          value={
            <span className="font-mono">{employee.bankAccount.accountNumber}</span>
          }
        />
        <Field
          label="Routing number"
          value={
            <span className="font-mono">
              {employee.bankAccount.routingNumber ?? "—"}
            </span>
          }
        />
      </Section>

      <Section title="Emergency contact">
        <Field label="Name" value={employee.emergencyContact.name} />
        <Field
          label="Relation"
          value={employee.emergencyContact.relation}
        />
        <Field label="Phone" value={employee.emergencyContact.phone} />
      </Section>

      <Section title="Notes">
        {employee.notes ? (
          <p className="whitespace-pre-line text-[13px] text-(--text-secondary)">
            {employee.notes}
          </p>
        ) : (
          <span className="text-[12px] text-(--text-tertiary)">
            No notes recorded.
          </span>
        )}
      </Section>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) p-4">
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) font-medium mb-3">
        {title}
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  );
}

function Field({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="text-[11px] text-(--text-tertiary) inline-flex items-center gap-1.5">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-[13px] text-(--text-primary) whitespace-pre-line">
        {value}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Attendance summary
 * ───────────────────────────────────────────────────────────── */

function AttendanceMonthlySummary({ attendance }: { attendance: MockAttendance[] }) {
  const summary = useMemo(() => {
    const map = new Map<
      string,
      {
        month: string;
        present: number;
        absent: number;
        leave: number;
        halfDay: number;
        overtimeMinutes: number;
      }
    >();
    for (const a of attendance) {
      const d = new Date(a.date);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      const slot =
        map.get(key) ??
        {
          month: key,
          present: 0,
          absent: 0,
          leave: 0,
          halfDay: 0,
          overtimeMinutes: 0,
        };
      if (a.status === "present") slot.present += 1;
      else if (a.status === "absent") slot.absent += 1;
      else if (a.status === "leave") slot.leave += 1;
      else if (a.status === "half_day") slot.halfDay += 1;
      slot.overtimeMinutes += a.overtimeMinutes;
      map.set(key, slot);
    }
    return Array.from(map.values()).sort((a, b) => (a.month < b.month ? 1 : -1));
  }, [attendance]);

  if (summary.length === 0) {
    return (
      <EmptyState
        title="No attendance recorded"
        description="Attendance entries will appear here once marked."
      />
    );
  }

  return (
    <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) overflow-hidden">
      <table className="w-full text-[12px] border-collapse">
        <thead>
          <tr className="text-(--text-tertiary)">
            <th className="text-left px-3 py-2 font-medium">Month</th>
            <th className="text-right px-3 py-2 font-medium">Present</th>
            <th className="text-right px-3 py-2 font-medium">Half day</th>
            <th className="text-right px-3 py-2 font-medium">Leave</th>
            <th className="text-right px-3 py-2 font-medium">Absent</th>
            <th className="text-right px-3 py-2 font-medium">Overtime</th>
          </tr>
        </thead>
        <tbody>
          {summary.map((s) => (
            <tr key={s.month} className="border-t border-(--border-subtle)">
              <td className="px-3 py-2 font-mono">{s.month}</td>
              <td className="px-3 py-2 text-right tabular-nums">{s.present}</td>
              <td className="px-3 py-2 text-right tabular-nums">{s.halfDay}</td>
              <td className="px-3 py-2 text-right tabular-nums">{s.leave}</td>
              <td className="px-3 py-2 text-right tabular-nums">{s.absent}</td>
              <td className="px-3 py-2 text-right tabular-nums">
                {(s.overtimeMinutes / 60).toFixed(1)}h
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Leaves table
 * ───────────────────────────────────────────────────────────── */

function LeavesTable({ leaves }: { leaves: MockLeave[] }) {
  const columns = useMemo<ColumnDef<MockLeave>[]>(
    () => [
      {
        accessorKey: "leaveType",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="neutral" size="sm" className="capitalize">
            {row.original.leaveType}
          </Badge>
        ),
      },
      {
        id: "range",
        header: "Range",
        cell: ({ row }) => (
          <span className="font-mono text-[12px]">
            {formatDate(row.original.fromDate, { style: "short" })} →{" "}
            {formatDate(row.original.toDate, { style: "short" })}
          </span>
        ),
      },
      {
        accessorKey: "days",
        header: "Days",
        meta: { numeric: true, mono: true },
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">{row.original.days}</span>
        ),
      },
      {
        accessorKey: "reason",
        header: "Reason",
        cell: ({ row }) => (
          <span className="text-(--text-secondary)">
            {row.original.reason ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusPill
            variant={LEAVE_STATUS_VARIANT[row.original.status]}
            size="sm"
          >
            {row.original.status}
          </StatusPill>
        ),
      },
    ],
    [],
  );

  if (leaves.length === 0) {
    return (
      <EmptyState
        title="No leaves yet"
        description="Leave requests will appear here once submitted."
      />
    );
  }

  return (
    <DataTable data={leaves} columns={columns} getRowId={(r) => r._id} />
  );
}

/* ─────────────────────────────────────────────────────────────
 * Payments table
 * ───────────────────────────────────────────────────────────── */

function PaymentsTable({
  payments,
  canSeeFinance,
}: {
  payments: MockEmployeePayment[];
  canSeeFinance: boolean;
}) {
  const columns = useMemo<ColumnDef<MockEmployeePayment>[]>(
    () => [
      {
        accessorKey: "paymentType",
        header: "Type",
        cell: ({ row }) => (
          <Badge variant="neutral" size="sm" className="capitalize">
            {row.original.paymentType}
          </Badge>
        ),
      },
      {
        id: "period",
        header: "Period",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-secondary)">
            {formatDate(row.original.periodFrom, { style: "short" })} →{" "}
            {formatDate(row.original.periodTo, { style: "short" })}
          </span>
        ),
      },
      {
        id: "base",
        header: "Base",
        meta: { numeric: true, mono: true, hideOnMobile: true },
        cell: ({ row }) =>
          canSeeFinance ? (
            <span className="font-mono tabular-nums">
              {formatMoney(
                row.original.baseSalaryCents,
                row.original.currency,
                { locale: "en-BD" },
              )}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "ot",
        header: "OT",
        meta: { numeric: true, mono: true, hideOnMobile: true },
        cell: ({ row }) =>
          canSeeFinance ? (
            <span className="font-mono tabular-nums text-(--text-secondary)">
              {formatMoney(row.original.overtimeCents, row.original.currency, {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "bonus",
        header: "Bonus",
        meta: { numeric: true, mono: true, hideOnMobile: true },
        cell: ({ row }) =>
          canSeeFinance ? (
            <span className="font-mono tabular-nums text-(--text-secondary)">
              {formatMoney(row.original.bonusCents, row.original.currency, {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "deductions",
        header: "Deductions",
        meta: { numeric: true, mono: true, hideOnMobile: true },
        cell: ({ row }) =>
          canSeeFinance ? (
            <span className="font-mono tabular-nums text-(--text-secondary)">
              {formatMoney(
                row.original.deductionsCents,
                row.original.currency,
                { locale: "en-BD" },
              )}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "net",
        header: "Net",
        meta: { numeric: true, mono: true },
        cell: ({ row }) =>
          canSeeFinance ? (
            <span className="font-mono tabular-nums text-(--text-primary)">
              {formatMoney(row.original.netCents, row.original.currency, {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusPill
            variant={PAYMENT_STATUS_VARIANT[row.original.status]}
            size="sm"
          >
            {row.original.status}
          </StatusPill>
        ),
      },
    ],
    [canSeeFinance],
  );

  if (payments.length === 0) {
    return (
      <EmptyState
        title="No payments yet"
        description="Payroll entries will appear here once recorded."
      />
    );
  }

  return (
    <DataTable
      data={payments}
      columns={columns}
      getRowId={(r) => r._id}
    />
  );
}

/* ─────────────────────────────────────────────────────────────
 * Invoices table
 * ───────────────────────────────────────────────────────────── */

function InvoicesTable({ invoices }: { invoices: MockEmployeeInvoice[] }) {
  if (invoices.length === 0) {
    return (
      <EmptyState
        title="No linked invoices"
        description="Travel reimbursements and other expenses tied to this employee will appear here."
      />
    );
  }
  return (
    <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) overflow-hidden">
      <table className="w-full text-[12px] border-collapse">
        <thead>
          <tr className="text-(--text-tertiary)">
            <th className="text-left px-3 py-2 font-medium">Date</th>
            <th className="text-left px-3 py-2 font-medium">Source</th>
            <th className="text-left px-3 py-2 font-medium">Reference</th>
            <th className="text-right px-3 py-2 font-medium">Amount</th>
            <th className="text-left px-3 py-2 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((i) => (
            <tr key={i._id} className="border-t border-(--border-subtle)">
              <td className="px-3 py-2 font-mono text-[12px]">
                {formatDate(i.occurredAt, { style: "short" })}
              </td>
              <td className="px-3 py-2 capitalize text-(--text-secondary)">
                {i.sourceTable.replace(/([A-Z])/g, " $1").toLowerCase()}
              </td>
              <td className="px-3 py-2 font-mono text-[11px] text-(--text-tertiary)">
                {i.sourceId}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {formatMoney(i.amountInBaseCents, "BDT", { locale: "en-BD" })}
              </td>
              <td className="px-3 py-2 text-(--text-secondary)">
                {i.notes ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * History tab — placeholder pointing at the Audit module.
 * ───────────────────────────────────────────────────────────── */

function HistoryTab() {
  return (
    <EmptyState
      title="Audit trail"
      description="Every change to this employee record is captured in the global audit log. Open the Audit module to see the full forensic history."
      action={
        <Button variant="outline" asChild>
          <a href="/audit">Open audit log</a>
        </Button>
      }
    />
  );
}
